import { AllocationScenarioValuationModel, ValuationApproachWeightModel } from 'api';
import { ALLOCATION_SCENARIO_TYPE_CURRENT_VALUE } from 'pages/ValuationsAllocation/common/constants/allocation';
import { getArrayValue, roundToN } from 'utilities';
import { RebalanceScenariosWeightsParams } from './types';

const WEIGHT_DECIMAL_PLACES = 6;

const isDeletedApproachWeight = (
  weight: ValuationApproachWeightModel,
  { approachId, approachUuid }: RebalanceScenariosWeightsParams['deletedApproach']
) => (approachUuid && weight.approach_uuid === approachUuid) || (approachId && weight.valuation_approach_id === approachId);

const getWeightValue = (weight: ValuationApproachWeightModel) => Number(weight.weight) || 0;

// Split the deleted weight evenly among approaches that still carry weight; 0% approaches stay excluded
// unless none carry weight, in which case every remaining approach gets a share.
const redistributeDeletedWeight = (
  remainingWeights: ValuationApproachWeightModel[],
  deletedWeight: number
): ValuationApproachWeightModel[] => {
  const hasWeightedRecipients = remainingWeights.some(weight => getWeightValue(weight) > 0);
  const isRecipient = (weight: ValuationApproachWeightModel) => !hasWeightedRecipients || getWeightValue(weight) > 0;
  const recipientsCount = remainingWeights.filter(isRecipient).length;
  if (deletedWeight <= 0 || recipientsCount === 0) {
    return remainingWeights;
  }

  const share = roundToN(deletedWeight / recipientsCount, WEIGHT_DECIMAL_PLACES);
  let recipientIndex = 0;
  return remainingWeights.map(weight => {
    if (!isRecipient(weight)) {
      return weight;
    }
    recipientIndex += 1;
    // Last recipient absorbs rounding so the column total is preserved.
    const added = recipientIndex === recipientsCount ? deletedWeight - share * (recipientsCount - 1) : share;
    return { ...weight, weight: roundToN(getWeightValue(weight) + added, WEIGHT_DECIMAL_PLACES).toString() };
  });
};

const rebalanceScenariosWeights = ({ allocationScenarios, deletedApproach }: RebalanceScenariosWeightsParams) =>
  allocationScenarios.map(scenario => {
    const weights = getArrayValue(scenario.allocations_scenarios_weights);
    const deletedWeights = weights.filter(weight => isDeletedApproachWeight(weight, deletedApproach));
    const remainingWeights = weights.filter(weight => !isDeletedApproachWeight(weight, deletedApproach));
    const deletedWeight = deletedWeights.reduce((sum, weight) => sum + getWeightValue(weight), 0);
    // Only Current Value scenarios weight several approaches; other types are pinned to their approach.
    const isCurrentValue = scenario.scenario_type === ALLOCATION_SCENARIO_TYPE_CURRENT_VALUE;

    return {
      ...scenario,
      allocations_scenarios_weights: isCurrentValue
        ? redistributeDeletedWeight(remainingWeights, deletedWeight)
        : remainingWeights,
    } as AllocationScenarioValuationModel;
  });

export default rebalanceScenariosWeights;
